import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { CalendarInput } from 'components/CalendarInput/CalendarInput'
import { Select, Option } from 'components/Select/Select'
import {
  getSimpleOperationalTeams,
  getOperationalTeamDetails,
} from 'features/operationalManagement/operationalManagementSlice'
import {
  FieldError,
  MetaChip,
  MetaChipLabel,
  MetaChipsRow,
  SectionCard,
  SectionSub,
  SectionTitle,
} from '../BulkAssign.styled'

const startOfToday = () => {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

const isBeforeToday = (d: Date) => {
  const today = startOfToday()
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  return day.getTime() < today.getTime()
}

interface Props {
  selectedCount: number
  targetTeamId: number | null
  onTargetTeamChange: (id: number | null) => void
  startDate: Date | null
  endDate: Date | null
  onStartDateChange: (d: Date | null) => void
  onEndDateChange: (d: Date | null) => void
  showErrors?: boolean
}

export const TargetDatesStep = ({
  selectedCount,
  targetTeamId,
  onTargetTeamChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  showErrors,
}: Props) => {
  const dispatch = useDispatch<AppDispatch>()
  const simpleTeams = useSelector(
    (state: RootState) => state.operationalManagement.simpleTeams,
  )
  const teamDetailsById = useSelector(
    (state: RootState) => state.operationalManagement.teamDetailsById,
  )

  useEffect(() => {
    dispatch(getSimpleOperationalTeams(undefined))
  }, [dispatch])

  useEffect(() => {
    if (targetTeamId != null && !teamDetailsById[targetTeamId]) {
      dispatch(getOperationalTeamDetails(targetTeamId))
    }
  }, [dispatch, targetTeamId, teamDetailsById])

  const teamOptions = useMemo<Option[]>(
    () => simpleTeams.map((t) => ({ label: t.name, value: t.id })),
    [simpleTeams],
  )
  const selectedOption = teamOptions.find((o) => o.value === targetTeamId) ?? null
  const details = targetTeamId != null ? teamDetailsById[targetTeamId] : undefined

  const supervisorLabel = details?.supervisors.length
    ? details.supervisors
        .slice(0, 2)
        .map((s) => `${s.user.firstName} ${s.user.lastName}`)
        .join(', ') +
      (details.supervisors.length > 2 ? `, +${details.supervisors.length - 2}` : '')
    : '—'

  const teamError = showErrors && targetTeamId == null ? 'Target team is required' : null
  const startError =
    showErrors && !startDate
      ? 'Effective Start is required'
      : startDate && isBeforeToday(startDate)
      ? 'Effective Start cannot be in the past'
      : null
  const endError =
    endDate && isBeforeToday(endDate)
      ? 'Effective End cannot be in the past'
      : endDate && startDate && endDate.getTime() < startDate.getTime()
      ? 'Effective End must be after Effective Start'
      : null

  return (
    <>
      <SectionCard>
        <SectionTitle>Target Team</SectionTitle>
        <SectionSub>
          {selectedCount} selected employees will be assigned to this team
        </SectionSub>
        <Select
          label='Team *'
          placeholder='Select Target Team'
          options={teamOptions}
          value={selectedOption}
          onChange={(opt) => onTargetTeamChange(opt.value as number)}
        />
        {teamError && <FieldError>{teamError}</FieldError>}
        {targetTeamId != null && (
          <MetaChipsRow>
            <MetaChip>
              <MetaChipLabel>SUPERVISOR</MetaChipLabel>
              {supervisorLabel}
            </MetaChip>
            <MetaChip>
              <MetaChipLabel>PARENT TEAM</MetaChipLabel>
              {details?.parentTeam?.name ?? '—'}
            </MetaChip>
          </MetaChipsRow>
        )}
      </SectionCard>

      <SectionCard>
        <SectionTitle>Effective Dates</SectionTitle>
        <SectionSub>All selected employees will be assigned to this team</SectionSub>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div>
            <CalendarInput
              width='100%'
              labelText='Effective Start'
              required
              nomarg
              defaultValue={startDate ?? undefined}
              minDate={startOfToday()}
              onChange={(d) => {
                const next = d instanceof Date ? d : null
                if (next && isBeforeToday(next)) return
                onStartDateChange(next)
              }}
            />
            {startError && <FieldError>{startError}</FieldError>}
          </div>
          <div>
            <CalendarInput
              width='100%'
              labelText='Effective End'
              nomarg
              defaultValue={endDate ?? undefined}
              minDate={startDate ?? startOfToday()}
              onChange={(d) => {
                const next = d instanceof Date ? d : null
                if (next && isBeforeToday(next)) return
                onEndDateChange(next)
              }}
            />
            {endError && <FieldError>{endError}</FieldError>}
          </div>
        </div>
      </SectionCard>
    </>
  )
}
